import {
  AdvancedEditorItem,
  AdvancedEditorItemTypeEnum,
  emptyId,
  Events,
  FlowResourceType,
  IAeSidebarProps,
  useEditorStore,
  useEvents,
} from '@diana/core';
import { useCallback } from 'react';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { TActiveComponent } from '../../../Store/FlowBuilder2PageStore.types';

export const useAeLayoutSync = (keepItemsByType?: IAeSidebarProps['keepItemsByType']) => {
  const setActiveComponent = useFlowBuilder2PageStore((store) => store.setActiveComponent);
  const setActiveTaskId = useFlowBuilder2PageStore((store) => store.setActiveTaskId);

  const getItem = useEditorStore((store) => store.getItem);
  const getBreadcrumbs = useEditorStore((store) => store.getBreadcrumbs);
  const getContent = useEditorStore((store) => store.getContent);
  const setSelectedItemId = useEditorStore((store) => store.setSelectedItemId);
  const setIsLoadingAeContent = useEditorStore((store) => store.setIsLoadingAeContent);
  const isAssetType = useEditorStore(
    (store) => store.flow?.properties.flowResourceType === FlowResourceType.Entity,
  );

  const autoExpandParents = useCallback(
    (itemId: string) => {
      const parentIds = getBreadcrumbs(itemId)
        .map((item) => item.id)
        .slice(0, -1);
      Events.ExpandAdvancedEditorNavItems.post({ itemIds: parentIds });
    },
    [getBreadcrumbs],
  );

  const getAllAeStepsAndTasks = useCallback((nestedItems: AdvancedEditorItem[]) => {
    return nestedItems.flatMap((item) =>
      item.type === AdvancedEditorItemTypeEnum.Step && Array.isArray(item.children)
        ? item.children
        : [item],
    );
  }, []);

  const findActiveTask = useCallback(
    ({
      items,
      activeId,
      selectByTaskId = true,
    }: {
      items: AdvancedEditorItem[];
      activeId: string;
      selectByTaskId?: boolean;
    }) => {
      return items.find((item) => {
        const content = getContent(item.id, false) as any;
        return selectByTaskId ? content?.taskId === activeId : content?.layoutId === activeId;
      });
    },
    [getContent],
  );

  const selectTask = useCallback(
    ({
      items,
      activeId,
      selectByTaskId = true,
    }: {
      items: AdvancedEditorItem[];
      activeId: string;
      selectByTaskId?: boolean;
    }) => {
      const allItems = getAllAeStepsAndTasks(items);
      const activeTaskAeItem = findActiveTask({ items: allItems, activeId, selectByTaskId });

      if (activeTaskAeItem) {
        setSelectedItemId(activeTaskAeItem.id);
        autoExpandParents(activeTaskAeItem.id);
      }
    },
    [findActiveTask, autoExpandParents, getAllAeStepsAndTasks, setSelectedItemId],
  );

  const selectComponent = useCallback(
    ({
      items,
      activeComponent,
    }: {
      items: AdvancedEditorItem[];
      activeComponent: TActiveComponent;
    }) => {
      const allItems = getAllAeStepsAndTasks(items);
      const activeTaskAeItem = findActiveTask({
        items: allItems,
        activeId: activeComponent.layoutId,
        selectByTaskId: false,
      });

      if (activeTaskAeItem) {
        const lazyModel = useEditorStore.getState().lazyModels[activeTaskAeItem.id];
        if (lazyModel && lazyModel.items.length > 0) {
          setSelectedItemId(activeComponent.componentId);
          autoExpandParents(activeComponent.componentId);
          return;
        }

        setSelectedItemId(activeTaskAeItem.id);
        const idsToExpand = getBreadcrumbs(activeTaskAeItem.id).map((item) => item.id);
        Events.ExpandAdvancedEditorNavItems.post({ itemIds: idsToExpand });
        setIsLoadingAeContent(true);
      }
    },
    [
      findActiveTask,
      autoExpandParents,
      getAllAeStepsAndTasks,
      getBreadcrumbs,
      setIsLoadingAeContent,
      setSelectedItemId,
    ],
  );

  const selectDefaultItemWithContent = useCallback(
    (nestedItems: AdvancedEditorItem[]) => {
      const findFirstItemWithContent = (items: AdvancedEditorItem[]): AdvancedEditorItem | null => {
        if (!items || items.length === 0) return null;

        for (const item of items) {
          const content = getContent(item.id, false);
          if (content && item.type !== AdvancedEditorItemTypeEnum.Step) return item;

          if (item.children?.length) {
            const found = findFirstItemWithContent(item.children);
            if (found) return found;
          }
        }
        return null;
      };

      const firstItemWithContent = findFirstItemWithContent(nestedItems);

      if (firstItemWithContent) {
        setSelectedItemId(firstItemWithContent.id);
        autoExpandParents(firstItemWithContent.id);
      } else if (nestedItems.length > 0) {
        setSelectedItemId(nestedItems[0].id);
      }
    },
    [getContent, setSelectedItemId, autoExpandParents],
  );

  const selectActivityItem = useCallback(
    (nestedItems: AdvancedEditorItem[]) => {
      const { activeTaskId, activeComponent } = useFlowBuilder2PageStore.getState();
      const activityItem = nestedItems.find(
        (item) => item.type === AdvancedEditorItemTypeEnum.Activities,
      );

      if (!activityItem) return;

      if (activeTaskId) {
        return selectTask({ items: activityItem.children, activeId: activeTaskId });
      }

      if (activeComponent) {
        return selectTask({
          items: activityItem.children,
          activeId: activeComponent.layoutId,
          selectByTaskId: false,
        });
      }

      selectDefaultItemWithContent(nestedItems);
    },
    [selectDefaultItemWithContent, selectTask],
  );

  const selectLayoutItem = useCallback(
    (nestedItems: AdvancedEditorItem[]) => {
      const { activeComponent, activeTaskId } = useFlowBuilder2PageStore.getState();

      if (!activeComponent && !activeTaskId) {
        selectDefaultItemWithContent(nestedItems);
      }

      if (activeTaskId) {
        selectTask({ items: nestedItems, activeId: activeTaskId });
      }

      if (activeComponent) {
        selectComponent({ items: nestedItems, activeComponent });
      }
    },
    [selectComponent, selectDefaultItemWithContent, selectTask],
  );

  const initialSetItemCallback = useCallback(
    (nestedItems: AdvancedEditorItem[]) => {
      const { enableAeAutoSelect, setEnableAeAutoSelect } = useEditorStore.getState();
      if (!enableAeAutoSelect) return;
      setEnableAeAutoSelect(false);

      switch (true) {
        case keepItemsByType && keepItemsByType.includes(AdvancedEditorItemTypeEnum.Steps):
          selectLayoutItem(nestedItems);
          break;

        case keepItemsByType && keepItemsByType.includes(AdvancedEditorItemTypeEnum.Activities):
          selectActivityItem(nestedItems);
          break;

        default:
          selectDefaultItemWithContent(nestedItems);
      }
    },
    [selectActivityItem, selectLayoutItem, selectDefaultItemWithContent, keepItemsByType],
  );

  const selectLayoutScope = useCallback(
    (itemId: string) => {
      const item = getItem(itemId);
      const layoutTypes = [
        AdvancedEditorItemTypeEnum.Step,
        AdvancedEditorItemTypeEnum.Task,
        AdvancedEditorItemTypeEnum.Section,
        AdvancedEditorItemTypeEnum.Component,
      ];
      if (!item || !layoutTypes.includes(item.type)) return;

      const breadcrumbs = getBreadcrumbs(itemId);
      const last = breadcrumbs[breadcrumbs.length - 1];

      const currentTask = breadcrumbs.find((b) => b.type === AdvancedEditorItemTypeEnum.Task);
      const content = getContent(currentTask?.id ?? '', false) as unknown as Record<string, any>;
      const layoutId = content?.layoutId ?? '';
      const taskId = content?.taskId ?? '';

      switch (last.type) {
        case AdvancedEditorItemTypeEnum.Component:
        case AdvancedEditorItemTypeEnum.Section: {
          setActiveComponent({ layoutId, componentId: last.id });
          break;
        }

        default: {
          if (currentTask && !isAssetType) setActiveTaskId(taskId);
          break;
        }
      }
    },
    [getItem, getContent, getBreadcrumbs, setActiveComponent, setActiveTaskId, isAssetType],
  );

  useEvents((ctx) => {
    Events.SyncAdvancedEditorActiveComponent.attach(ctx, ({ items }) => {
      const activeComponentId = useFlowBuilder2PageStore.getState().activeComponent?.componentId;
      const activeItem = items.find((item) => item.id === activeComponentId);
      if (activeComponentId && activeItem && activeItem.id === activeComponentId) {
        setSelectedItemId(activeComponentId);
        if (activeItem.parentId !== emptyId) {
          setTimeout(() => {
            Events.ExpandAdvancedEditorNavItems.post({ itemIds: [activeItem.parentId] });
          });
        }
      }
    });
  }, []);

  return { initialSetItemCallback, selectLayoutScope };
};
